/**
 * Kit de soporte v1 — ReportarProblemaDialog, el formulario "Reportar un
 * problema" (ADR-0025).
 *
 * Mismos campos y textos que el de analyzer y proveedores: arriba la entidad
 * de la pantalla (llenada desde la ruta y editable; no aparece si la app no
 * tiene una), "¿Qué pasó?" y una captura opcional en la misma zona (arrastrar,
 * clic o pegar), una nota de privacidad y el folio en el mismo diálogo al
 * enviar. Modal propio (portal + scrim) con Escape y foco atrapado; un clic
 * afuera NO lo cierra. El borrador vive en el botón, así cerrar o un envío
 * fallido nunca pierden el texto, y un reintento usa el mismo `report_id`.
 */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2 } from "lucide-react";

import type { SoporteConfig } from "./config";
import { EvidenceDropzone } from "./EvidenceDropzone";
import {
  DESC_MAX,
  DESC_MIN,
  IMAGE_TYPES,
  MAX_CAPTURA_MB,
  type ReportDraft,
} from "./reportDraft";
import { EnvioError, enviarReporte } from "./send";
import { buildReportContext } from "./supportContext";

const BUTTON =
  "inline-flex items-center justify-center gap-2 rounded px-2.5 py-1.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50";
const PRIMARY = `${BUTTON} bg-brand text-brand-fg hover:bg-brand-hover`;
const SECONDARY = `${BUTTON} border border-border bg-card text-fg hover:bg-card-2`;
const FIELD =
  "block w-full rounded border border-border bg-card px-3 py-2 text-sm text-fg placeholder:text-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-soft";

export interface ReportarProblemaDialogProps {
  config: SoporteConfig;
  draft: ReportDraft;
  onDraftChange: (draft: ReportDraft) => void;
  reportId: string;
  pathname: string;
  /** Folio del reporte recién enviado; muestra el mensaje de éxito. */
  sentFolio: string | null;
  onClose: () => void;
  onSent: (folio: string) => void;
}

function formatError(error: unknown): string {
  if (error instanceof EnvioError && error.status > 0) {
    if ([413, 415, 422, 429, 503].includes(error.status)) {
      return error.message || "Revisa el formulario y la captura.";
    }
    return "No se pudo enviar el reporte. Tu texto se conserva: intenta de nuevo en un momento.";
  }
  return "No se pudo enviar el reporte. Revisa tu conexión e intenta de nuevo.";
}

export function ReportarProblemaDialog({
  config,
  draft,
  onDraftChange,
  reportId,
  pathname,
  sentFolio,
  onClose,
  onSent,
}: ReportarProblemaDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [errorCaptura, setErrorCaptura] = useState<string | null>(null);
  const entidad = config.entidad;

  const setCaptura = (file: File | undefined) => {
    setErrorCaptura(null);
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      setErrorCaptura("La captura debe ser PNG, JPG o WEBP.");
      return;
    }
    if (file.size > MAX_CAPTURA_MB * 1024 * 1024) {
      setErrorCaptura(`La captura debe pesar menos de ${MAX_CAPTURA_MB} MB.`);
      return;
    }
    onDraftChange({ ...draft, captura: file });
  };

  useEffect(() => {
    const panel = panelRef.current;
    panel?.querySelector<HTMLElement>("#bug-report-descripcion")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pending) {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !panel) return;
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'textarea, input:not([type="file"]), [role="button"][tabindex="0"], button:not([disabled])',
        ),
      );
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [pending, onClose]);

  const descripcion = draft.descripcion.trim();
  const valido = descripcion.length >= DESC_MIN;

  const enviar = async (event: FormEvent) => {
    event.preventDefault();
    if (!valido || pending) return;
    setPending(true);
    setError(null);
    try {
      const accepted = await enviarReporte(
        config,
        {
          ...buildReportContext(pathname),
          report_id: reportId,
          descripcion,
          entidad_id: entidad ? draft.entidadId.trim() || null : null,
        },
        draft.captura,
      );
      onSent(accepted.folio);
    } catch (err) {
      setError(err);
    } finally {
      setPending(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reportar-problema-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--scrim)] p-4"
    >
      <div
        ref={panelRef}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-modal border border-border bg-card shadow-xl"
      >
        <header className="border-b border-border p-5">
          <h2
            id="reportar-problema-title"
            className="text-base font-semibold text-fg"
          >
            Reportar un problema
          </h2>
        </header>

        {sentFolio ? (
          <div className="space-y-4 p-5">
            <div
              role="status"
              className="flex items-center gap-3 py-4 text-sm text-fg"
            >
              <CheckCircle2
                aria-hidden
                className="h-5 w-5 shrink-0 text-ok-fg"
              />
              <span>
                Reporte {sentFolio} enviado. Gracias, el equipo lo revisará.
              </span>
            </div>
            <div className="flex justify-end">
              <button type="button" className={SECONDARY} onClick={onClose}>
                Cerrar
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(event) => void enviar(event)}
            noValidate
            onPaste={(event) => {
              const file = Array.from(event.clipboardData.files)[0];
              if (file) {
                event.preventDefault();
                setCaptura(file);
              }
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              setCaptura(Array.from(event.dataTransfer.files)[0]);
            }}
            className="space-y-4 p-5 text-fg"
          >
            {entidad ? (
              <div className="space-y-1">
                <label
                  htmlFor="bug-report-entidad"
                  className="text-sm font-medium"
                >
                  {entidad.etiqueta}
                </label>
                <input
                  id="bug-report-entidad"
                  value={draft.entidadId}
                  maxLength={64}
                  onChange={(e) =>
                    onDraftChange({ ...draft, entidadId: e.target.value })
                  }
                  placeholder={entidad.placeholder}
                  disabled={pending}
                  className={`${FIELD} font-mono placeholder:font-sans`}
                />
              </div>
            ) : null}

            <div className="space-y-1">
              <label
                htmlFor="bug-report-descripcion"
                className="text-sm font-medium"
              >
                ¿Qué pasó?
              </label>
              <textarea
                id="bug-report-descripcion"
                rows={5}
                maxLength={DESC_MAX}
                value={draft.descripcion}
                onChange={(e) =>
                  onDraftChange({ ...draft, descripcion: e.target.value })
                }
                placeholder="Qué estabas haciendo, qué pasó y qué esperabas que pasara."
                disabled={pending}
                aria-describedby="bug-report-contador"
                className={`${FIELD} resize-y`}
              />
              <div
                id="bug-report-contador"
                className="flex justify-between text-xs text-muted"
              >
                <span>{valido ? "" : `Mínimo ${DESC_MIN} caracteres.`}</span>
                <span>
                  {draft.descripcion.length}/{DESC_MAX}
                </span>
              </div>
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium">
                Captura de pantalla (opcional)
              </p>
              <p className="text-xs text-muted">
                Súbela, arrástrala o pégala aquí con Ctrl+V / Cmd+V.
              </p>
              <EvidenceDropzone
                value={draft.captura}
                onFile={setCaptura}
                onRemove={() => onDraftChange({ ...draft, captura: null })}
                error={errorCaptura}
                disabled={pending}
              />
              {errorCaptura ? (
                <p role="alert" className="text-xs text-err-fg">
                  {errorCaptura}
                </p>
              ) : null}
            </div>

            <p className="text-xs text-muted">
              Además de tu descripción se envían la pantalla actual, la versión
              de la plataforma, tu navegador y los últimos errores técnicos. No
              incluyas contraseñas en la captura.
            </p>

            {error ? (
              <p role="alert" className="text-sm text-err-fg">
                {formatError(error)}
              </p>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className={SECONDARY}
                onClick={onClose}
                disabled={pending}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={PRIMARY}
                disabled={!valido || pending}
              >
                {pending ? "Enviando…" : "Enviar reporte"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
